import React, { type ComponentType, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import JoinEmailCampaignBulkSelectionForm from "./JoinEmailCampaignBulkSelectionForm";

type LocationId = string | number;

type EmailTemplate = {
  id: LocationId;
  [key: string]: unknown;
};

type EmailCampaign = {
  [key: string]: unknown;
};

type EmailLocation = {
  id: LocationId;
  [key: string]: unknown;
};

type BulkSelectionFormData = Record<string, unknown>;

type JoinEmailCampaignBulkSelectionProps = {
  templates?: EmailTemplate[];
  campaign?: EmailCampaign;
  locations?: EmailLocation[];
  setLocationExpanded: (_locations: LocationId[]) => void;
  setShowCheckbox: (_show: boolean) => void;
  setFieldValue: (
    _locationId: LocationId,
    _value: BulkSelectionFormData & { locationInfo?: EmailLocation }
  ) => void;
  checkedLocations?: LocationId[];
};

type BulkSelectionFormProps = {
  templates?: EmailTemplate[];
  onSubmit: (_formData: BulkSelectionFormData) => void;
  campaign?: EmailCampaign;
  locations?: EmailLocation[];
  isLocationChecked?: number;
};

const BulkSelectionForm =
  JoinEmailCampaignBulkSelectionForm as ComponentType<BulkSelectionFormProps>;

function JoinEmailCampaignBulkSelection({
  templates,
  campaign,
  locations,
  setLocationExpanded,
  setShowCheckbox,
  setFieldValue,
  checkedLocations,
}: JoinEmailCampaignBulkSelectionProps) {
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleOnExpand =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
      if (isExpanded) setLocationExpanded([]);
      setShowCheckbox(isExpanded);
    };

  const shouldExpandIt = (panel: string) => expanded === panel;

  const handleBulkSubmit = (formData: BulkSelectionFormData) => {
    checkedLocations?.forEach((l) => {
      setFieldValue(l, {
        ...formData,
        locationInfo: locations?.find((loc) => loc.id === l),
      });
    });
  };

  const isLocationChecked = useMemo(() => {
    return checkedLocations?.length;
  }, [checkedLocations]);

  return (
    <div>
      <Accordion
        expanded={shouldExpandIt("bulkSelection")}
        onChange={handleOnExpand("bulkSelection")}
        className={`location flex-grow-1`}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          className="location-header"
        >
          {expanded ? (
            <Typography>
              1. Make selections for{" "}
              <span className="fw-b">template and schedule</span>
            </Typography>
          ) : (
            <Typography>
              <Typography
                component="span"
                color="textPrimary"
                className="fw-b mr-1"
              >
                Want to Email in bulk?{" "}
              </Typography>
              <Typography component="span" className="link">
                Click here
              </Typography>
            </Typography>
          )}
        </AccordionSummary>
        <AccordionDetails className="location-details">
          <BulkSelectionForm
            templates={templates}
            onSubmit={handleBulkSubmit}
            campaign={campaign}
            locations={locations}
            isLocationChecked={isLocationChecked}
          />
        </AccordionDetails>
      </Accordion>
    </div>
  );
}

export default JoinEmailCampaignBulkSelection;
